import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Edit2, Trash2, X, Save, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ehTipoProfissional, rotuloTipo } from "@/lib/tipoProfissional";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import { formatBRL, formatarData, diaDaSemana, nomeMes } from "@/lib/utils";

type SortKey = "data" | "dia" | "tipo" | "horario" | "valor";
type SortDir = "asc" | "desc";

export function Remuneracao() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);
  const [profissionalIdSel, setProfissionalIdSel] = useState<string>("todos");

  const [sortKey, setSortKey] = useState<SortKey>("data");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [editingTurno, setEditingTurno] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const utils = trpc.useUtils();
  const profissionaisQ = trpc.profissionais.listar.useQuery({ incluirInativos: false });
  const remunMesQ = trpc.remuneracao.porMes.useQuery({ ano, mes });
  const totalGeralQ = trpc.remuneracao.totalGeralMes.useQuery({ ano, mes });
  const tiposTurnoQ = trpc.tiposTurno.listar.useQuery({ incluirInativos: false });

  // Admin escolhe o profissional; não-admin fica travado no próprio (backend também só devolve o dele).
  const profId = isAdmin
    ? profissionalIdSel === "todos"
      ? null
      : Number(profissionalIdSel)
    : (remunMesQ.data?.[0]?.profissionalId ?? null);
  const detalheQ = trpc.remuneracao.detalhesProfissionalMes.useQuery(
    profId ? { profissionalId: profId, ano, mes } : (undefined as never),
    { enabled: !!profId },
  );

  const refresh = () => {
    if (profId) utils.remuneracao.detalhesProfissionalMes.invalidate();
    utils.remuneracao.porMes.invalidate();
    utils.remuneracao.totalGeralMes.invalidate();
  };
  const atualizarTurno = trpc.turnos.atualizar.useMutation({ onSuccess: () => { refresh(); setEditingTurno(null); } });
  const excluirTurno = trpc.turnos.excluir.useMutation({ onSuccess: () => { refresh(); setConfirmDelete(null); } });

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) { novo = 12; novoAno--; }
    if (novo > 12) { novo = 1; novoAno++; }
    setMes(novo);
    setAno(novoAno);
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  const turnosOrdenados = useMemo(() => {
    const arr = [...(detalheQ.data?.turnos ?? [])];
    const cmp = (a: any, b: any) => {
      let av: any, bv: any;
      switch (sortKey) {
        case "data": av = a.data; bv = b.data; break;
        case "dia": av = new Date(a.data + "T00:00:00").getUTCDay(); bv = new Date(b.data + "T00:00:00").getUTCDay(); break;
        case "tipo": av = a.tipoTurnoNome; bv = b.tipoTurnoNome; break;
        case "horario": av = a.horaInicio; bv = b.horaInicio; break;
        case "valor": av = Number(a.valor); bv = Number(b.valor); break;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    };
    return arr.sort(cmp);
  }, [detalheQ.data?.turnos, sortKey, sortDir]);

  const totaisAgg = useMemo(() => {
    const linhas = remunMesQ.data ?? [];
    const filtradas = profId ? linhas.filter((r) => r.profissionalId === profId) : linhas;
    return {
      totalTurnos: filtradas.reduce((s, r) => s + r.totalTurnos, 0),
      remuneracaoTurnos: filtradas.reduce((s, r) => s + Number(r.remuneracaoTotal), 0),
      diasTrabalhados: filtradas.reduce((s, r) => s + r.diasTrabalhados, 0),
    };
  }, [remunMesQ.data, profId]);

  const mediaPorDia = totaisAgg.diasTrabalhados > 0 ? totaisAgg.totalTurnos / totaisAgg.diasTrabalhados : 0;

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="ml-1 inline h-3 w-3" /> : <ArrowDown className="ml-1 inline h-3 w-3" />;
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Remuneração</h1>
        <p className="text-sm text-slate-500">Acompanhe sua remuneração mensal.</p>
      </div>

      {isAdmin && (
        <Card>
          <CardHeader><CardTitle>Selecionar profissional</CardTitle></CardHeader>
          <CardContent>
            <Select value={profissionalIdSel} onValueChange={setProfissionalIdSel}>
              <SelectTrigger className="w-full sm:w-72"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {(profissionaisQ.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
          {nomeMes(mes)} {ano}
        </div>
        <Button variant="outline" size="icon" onClick={() => navegar(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Total de Turnos" value={totaisAgg.totalTurnos} />
        <Stat label="Remuneração Turnos" value={formatBRL(totaisAgg.remuneracaoTurnos)} />
        <Stat label="Dias Trabalhados" value={totaisAgg.diasTrabalhados} />
        <Stat label="Média por Dia" value={mediaPorDia.toFixed(2)} />
        <Stat label="Total Geral" value={formatBRL(totalGeralQ.data?.total ?? 0)} highlight />
      </div>

      {profId && detalheQ.data?.profissional && (
        <Card>
          <CardHeader>
            <CardTitle>Detalhes do profissional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Nome</span><span className="font-medium">{detalheQ.data.profissional.nome}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tipo</span>
              <Badge variant={ehTipoProfissional(detalheQ.data.profissional.tipo) ? detalheQ.data.profissional.tipo : "outline"}>
                {rotuloTipo(detalheQ.data.profissional.tipo)}
              </Badge>
            </div>
            <div className="flex justify-between"><span className="text-slate-500">Status</span>
              <Badge variant={detalheQ.data.profissional.ativo ? "ativo" : "inativo"}>
                {detalheQ.data.profissional.ativo ? "Ativo" : "Inativo"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {profId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Turnos realizados</CardTitle>
              <span className="text-xs text-slate-400">Clique em uma coluna pra ordenar · clique na linha pra editar/excluir</span>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("data")}>Data<SortIcon k="data"/></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("dia")}>Dia<SortIcon k="dia"/></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("tipo")}>Tipo de Turno<SortIcon k="tipo"/></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("horario")}>Horário<SortIcon k="horario"/></TableHead>
                  <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("valor")}>Valor<SortIcon k="valor"/></TableHead>
                  {isAdmin && <TableHead className="w-24 text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {turnosOrdenados.map((t) => (
                  <TableRow key={t.turnoId} className="group hover:bg-slate-50">
                    <TableCell>{formatarData(t.data)}</TableCell>
                    <TableCell>{diaDaSemana(t.data)}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.tipoTurnoCor }} />
                        {t.tipoTurnoNome}
                      </span>
                    </TableCell>
                    <TableCell>{t.horaInicio} – {t.horaFim}</TableCell>
                    <TableCell className="text-right font-semibold text-brand-600">{formatBRL(t.valor)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {t.valorAcionado != null && (
                            t.valorPago != null ? (
                              <Button size="sm" variant="outline"
                                className="h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                title="Foi acionada — clique pra desfazer"
                                onClick={() => atualizarTurno.mutate({ id: t.turnoId, valorPago: null })}>
                                Acionada ✓
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7"
                                title={`Marcar como acionada (${formatBRL(Number(t.valorAcionado))})`}
                                onClick={() => atualizarTurno.mutate({ id: t.turnoId, valorPago: Number(t.valorAcionado) })}>
                                Acionar
                              </Button>
                            )
                          )}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <Button size="icon" variant="ghost" title="Editar / substituir"
                              onClick={() => setEditingTurno(t)} className="h-7 w-7">
                              <Edit2 className="h-3.5 w-3.5"/>
                            </Button>
                            <Button size="icon" variant="ghost" title="Excluir"
                              onClick={() => setConfirmDelete(t)}
                              className="h-7 w-7 text-red-500 hover:text-red-700">
                              <Trash2 className="h-3.5 w-3.5"/>
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!turnosOrdenados.length && (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-slate-400">Nenhum turno neste mês.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Modal Editar/Substituir turno */}
      {editingTurno && (
        <EditTurnoModal
          turno={editingTurno}
          tiposTurno={tiposTurnoQ.data ?? []}
          onClose={() => setEditingTurno(null)}
          onSave={(payload) => atualizarTurno.mutate(payload)}
          saving={atualizarTurno.isPending}
        />
      )}

      {/* Modal confirmar exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500"/>
              <h3 className="text-base font-semibold">Excluir turno?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              <strong>{formatarData(confirmDelete.data)}</strong> · {confirmDelete.tipoTurnoNome} ({confirmDelete.horaInicio}–{confirmDelete.horaFim})
              <br/>
              <span className="text-xs text-slate-400">Valor: {formatBRL(confirmDelete.valor)}</span>
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => excluirTurno.mutate({ id: confirmDelete.turnoId })}
                disabled={excluirTurno.isPending}>
                {excluirTurno.isPending ? "Excluindo..." : "Excluir"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditTurnoModal({
  turno, tiposTurno, onClose, onSave, saving,
}: {
  turno: any;
  tiposTurno: any[];
  onClose: () => void;
  onSave: (payload: { id: number; tipoTurnoId?: number; data?: string }) => void;
  saving: boolean;
}) {
  const [novoTipoId, setNovoTipoId] = useState<number>(turno.tipoTurnoId);
  const [novaData, setNovaData] = useState<string>(turno.data);

  const tipoOriginal = tiposTurno.find((t) => t.id === turno.tipoTurnoId);
  const tipoNovo = tiposTurno.find((t) => t.id === novoTipoId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Editar / substituir turno</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4"/></button>
        </div>

        <div className="mb-3 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
          Atual: <strong>{tipoOriginal?.nome}</strong> ({tipoOriginal?.horaInicio}–{tipoOriginal?.horaFim}) · {formatarData(turno.data)}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Data</label>
            <input type="date" value={novaData} onChange={(e) => setNovaData(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"/>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Tipo de turno</label>
            <Select value={String(novoTipoId)} onValueChange={(v) => setNovoTipoId(Number(v))}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                {tiposTurno.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.nome} · {t.horaInicio}–{t.horaFim} · {formatBRL(Number(t.valor))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tipoNovo && tipoNovo.id !== turno.tipoTurnoId && (
              <p className="mt-1 text-xs text-blue-600">
                ↪ Substituindo por <strong>{tipoNovo.nome}</strong>: novo valor {formatBRL(Number(tipoNovo.valor))}
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => {
            const payload: any = { id: turno.turnoId };
            if (novoTipoId !== turno.tipoTurnoId) payload.tipoTurnoId = novoTipoId;
            if (novaData !== turno.data) payload.data = novaData;
            if (!payload.tipoTurnoId && !payload.data) { onClose(); return; }
            onSave(payload);
          }} disabled={saving}>
            <Save className="mr-1 h-3.5 w-3.5"/>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-bold ${highlight ? "text-brand-600" : "text-slate-900"}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
